import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from 'react'

import { clearAuthTokens } from '../token-storage'
import { buildSessionState, EMPTY_SESSION, type Session } from './config'

export interface SessionContextProps {
  state: Session
  authenticateSession: () => void
  clearSession: () => void
}

const SessionContext = createContext<SessionContextProps | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const { isUserAuthenticated, isUserRoot, userId } = buildSessionState()

  const [session, setSession] = useState<Session>({
    isUserAuthenticated,
    isUserRoot,
    userId,
  })

  const authenticateSession = useCallback(() => {
    setSession(buildSessionState())
  }, [])

  const clearSession = useCallback(() => {
    clearAuthTokens()
    setSession(EMPTY_SESSION)
  }, [])

  return (
    <SessionContext
      value={{
        state: session,
        authenticateSession,
        clearSession,
      }}
    >
      {children}
    </SessionContext>
  )
}

export const useSession = () => {
  const context = useContext(SessionContext)

  if (!context) {
    throw new Error('useSession must be used within a SessionProvider')
  }

  return context
}
